// Resolves SRI hashes for optional runtime scripts and worker assets.
// Explicit policy pins win while cross-origin loads can fall back to one manifest.
import { readRuntimeSecurityPolicy } from './url-policy'
import { resolveRuntimeCoreScriptInfo } from './core-script'
import {
  getRuntimeAssetFilename,
  isSameOriginRuntimeUrl,
  isSafeRuntimeNetworkUrl,
  normalizeRuntimeAllowedOrigins,
  resolveRuntimeSecurityUrl,
} from './url-shared'

type RuntimeIntegrityMap = Record<string, string>

const integrityManifestCache = new Map<string, Promise<RuntimeIntegrityMap | null>>()

// Exposes the SRI hash for one runtime script URL when policy or manifest provides it.
export async function resolveRuntimeScriptIntegrity(rawUrl: string, baseHref: string): Promise<string | undefined> {
  const resolved = resolveRuntimeSecurityUrl(rawUrl, baseHref)
  // Stop integrity resolution when the target URL cannot be parsed safely.
  if (!resolved) return undefined

  const policy = readRuntimeSecurityPolicy()
  const explicit = resolveIntegrityFromMap(policy.cdnScriptIntegrity, resolved)
  // Return the explicit policy hash before touching the integrity manifest.
  if (explicit) return explicit

  // Keep same-origin runtime scripts on the current no-manifest path.
  if (isSameOriginRuntimeUrl(resolved)) return undefined

  const manifestUrl = resolveIntegrityManifestUrl(policy, baseHref)
  // Stop manifest fallback when no allowed integrity-manifest URL is available.
  if (!manifestUrl) return undefined

  const manifest = await loadRuntimeIntegrityManifest(manifestUrl)
  // Stop fallback when the manifest could not be loaded or normalized.
  if (!manifest) return undefined

  return resolveIntegrityFromMap(manifest, resolved)
}

// Resolve the integrity-manifest URL under the core-script base or policy override.
function resolveIntegrityManifestUrl(
  policy: ReturnType<typeof readRuntimeSecurityPolicy>,
  baseHref: string,
): string | undefined {
  const rawManifestUrl = policy.cdnScriptIntegrityManifest?.trim()
  // Stop manifest URL resolution when policy does not define one manifest path.
  if (!rawManifestUrl) return undefined

  const manifestBase = resolveRuntimeCoreScriptInfo()?.baseHref ?? baseHref
  const resolved = resolveRuntimeSecurityUrl(rawManifestUrl, manifestBase)
  // Reject manifest URLs that cannot be parsed safely.
  if (!resolved || !isSafeRuntimeNetworkUrl(resolved)) return undefined

  // Allow same-origin manifests without additional allowlist checks.
  if (isSameOriginRuntimeUrl(resolved)) return resolved.href

  const allowlist = normalizeRuntimeAllowedOrigins(policy.cdnScriptAllowlist)
  // Return the manifest URL only when its origin is explicitly allowed.
  if (allowlist.has(resolved.origin)) return resolved.href
  return undefined
}

// Load and cache one runtime integrity manifest by absolute URL.
async function loadRuntimeIntegrityManifest(manifestUrl: string): Promise<RuntimeIntegrityMap | null> {
  const cached = integrityManifestCache.get(manifestUrl)
  // Reuse in-flight integrity-manifest requests for duplicate lookups.
  if (cached) return cached

  const nextPromise = fetchRuntimeIntegrityManifest(manifestUrl)
  integrityManifestCache.set(manifestUrl, nextPromise)
  return nextPromise
}

// Fetch one runtime integrity manifest and normalize its hash-map payload.
async function fetchRuntimeIntegrityManifest(manifestUrl: string): Promise<RuntimeIntegrityMap | null> {
  // Stop manifest loading when `fetch` is unavailable in the current runtime.
  if (typeof fetch !== 'function') return null

  try {
    const response = await fetch(manifestUrl)
    // Reject non-2xx manifest responses to keep cross-origin loads fail-closed.
    if (!response.ok) return null

    return normalizeIntegrityManifest(await response.json() as unknown)
  } catch {
    return null
  }
}

// Normalize one manifest JSON payload into one trimmed integrity hash map.
function normalizeIntegrityManifest(payload: unknown): RuntimeIntegrityMap | null {
  // Reject non-object manifest payload values.
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null

  const manifest: RuntimeIntegrityMap = {}
  // Keep only non-empty string hash entries from the manifest payload.
  for (const [key, value] of Object.entries(payload)) {
    // Ignore non-string integrity values.
    if (typeof value !== 'string') continue
    const normalized = value.trim()
    // Ignore empty integrity strings after trim normalization.
    if (!normalized) continue
    manifest[key] = normalized
  }

  return manifest
}

// Resolve one integrity hash by absolute URL, pathname, or asset filename.
function resolveIntegrityFromMap(map: RuntimeIntegrityMap | undefined, resolved: URL): string | undefined {
  // Stop lookup when no integrity map is available.
  if (!map) return undefined

  const filename = getRuntimeAssetFilename(resolved)
  const byAbsolute = map[resolved.href]?.trim()
  // Return the most specific absolute-URL integrity hash first.
  if (byAbsolute) return byAbsolute

  const byPath = map[resolved.pathname]?.trim()
  // Return the pathname integrity hash when configured.
  if (byPath) return byPath

  const byFilename = map[filename]?.trim()
  return byFilename || undefined
}
